/**
 * Secure Photo Naming System
 * Generates 100% hash-based filenames with zero exposed user information
 * 
 * Structure: {userHash}/{imageHash}-{variant}.jpg
 * Example: "a3f2d8c1b4e5f6a7/9c8b7a6f5e4d3c2b-2.jpg"
 */

import { debugLogger } from '@/utils/debugLogger';

/**
 * Generate secure image hash from timestamp + random UUID
 * @returns 16-character hexadecimal hash
 */
export const generateSecureImageHash = async (timestamp: number): Promise<string> => {
  try {
    const randomId = crypto.randomUUID();
    const data = `${timestamp}-${randomId}`;
    
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(data));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Truncate to 16 characters for shorter URLs
    const hash = hashHex.slice(0, 16);
    
    debugLogger.debug('securePhotoNaming', 'Generated image hash', {
      timestamp,
      hashLength: hash.length,
      hash
    });
    
    return hash;
  } catch (error) {
    debugLogger.error('securePhotoNaming', 'Failed to generate image hash', error);
    throw error;
  }
};

/**
 * Generate secure user hash from userId
 * @returns 16-character hexadecimal hash
 */
export const generateUserHash = async (userId: string): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(userId));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    
    // Truncate to 16 characters
    const hash = hashHex.slice(0, 16);
    
    debugLogger.debug('securePhotoNaming', 'Generated user hash', {
      userIdLength: userId.length,
      hashLength: hash.length
    });
    
    return hash;
  } catch (error) {
    debugLogger.error('securePhotoNaming', 'Failed to generate user hash', error);
    throw error;
  }
};

/**
 * Generate complete secure photo path with hash-based naming (v1.5 format)
 * @param userId - User ID (will be hashed)
 * @param variant - Numeric variant (0-3)
 * @returns Object with full path and imageHash for reference
 */
export const generateSecurePhotoPath = async (
  userId: string,
  variant: 0 | 1 | 2 | 3
): Promise<{ path: string; imageHash: string; userHash: string }> => {
  try {
    const userHash = await generateUserHash(userId);
    const imageHash = await generateSecureImageHash(Date.now());
    const path = `${userHash}/${imageHash}_${variant}.jpg`;
    
    debugLogger.debug('securePhotoNaming', 'Generated secure photo path (v1.5)', {
      variant,
      path,
      pathLength: path.length
    });
    
    return { path, imageHash, userHash };
  } catch (error) {
    debugLogger.error('securePhotoNaming', 'Failed to generate secure photo path', error);
    throw error;
  }
};
